import { type Ref, ref, watch, provide } from 'vue';
import type { TabTransitionDirection } from 'src/design_system/components/tab/types';
import {
  MERIDIA_TABS_REGISTER_TAB,
  MERIDIA_TABS_UNREGISTER_TAB,
  MERIDIA_TABS_TRANSITION_DIRECTION,
} from 'src/design_system/components/tab/types';

export function useTabTransition(modelValue: Ref<string | number>): void {
  const tabOrder = ref<(string | number)[]>([]);

  /**
   * Registers a tab value for order tracking. Tab order is determined by
   * mount order (the sequence in which tabs call registerTab).
   *
   * Note: If a tab is conditionally rendered with v-if and re-mounts,
   * it will be registered at the end of the order array, not at its original
   * DOM position. This may cause incorrect transition directions. If you need
   * to toggle tab visibility, prefer v-show over v-if, or accept that
   * transition direction may be inaccurate for re-mounted tabs.
   */
  function registerTab(value: string | number): void {
    if (!tabOrder.value.includes(value)) {
      tabOrder.value.push(value);
    }
  }

  function unregisterTab(value: string | number): void {
    const index = tabOrder.value.indexOf(value);
    if (index !== -1) {
      tabOrder.value.splice(index, 1);
    }
  }

  const transitionDirection = ref<TabTransitionDirection>('none');

  watch(
    () => modelValue.value,
    (newValue, oldValue) => {
      if (newValue == null || oldValue == null) {
        transitionDirection.value = 'none';
        return;
      }

      const newIndex = tabOrder.value.indexOf(newValue);
      const oldIndex = tabOrder.value.indexOf(oldValue);

      if (newIndex === -1 || oldIndex === -1) {
        transitionDirection.value = 'none';
        return;
      }

      transitionDirection.value = newIndex > oldIndex ? 'forward' : 'backward';
    }
  );

  provide(MERIDIA_TABS_REGISTER_TAB, registerTab);
  provide(MERIDIA_TABS_UNREGISTER_TAB, unregisterTab);
  provide(MERIDIA_TABS_TRANSITION_DIRECTION, transitionDirection);
}
